import { FC, FormEvent, useContext, useState } from 'react';
import styled from 'styled-components';
import { Button, Stack, TextField, Typography } from '@mui/material';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { useYupValidationResolver } from '@vs-online-only-app/ui';
import {
  AuthenticationContext,
  useUpdateAssociate,
} from '@vs-online-only-app/state';
import { ProfileSetupContext } from '../ProfileSetupContext';
import ProfileSetupError from '../ProfileSetupError';
import ProfileSetupContinueButton from '../ProfileSetupContinueButton';
import ProfileSetupBackButton from '../ProfileSetupBackButton';

const StyledForm = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
`;

const validationSchema = yup.object({
  employeeId: yup.string().required('Required'),
});

export type EmployeeIdFormFieldValues = {
  employeeId: string;
};

const EnterEmployeeId: FC = () => {
  const [error, setError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);

  const { user, refetchUser } = useContext(AuthenticationContext);
  const { goToNextStep } = useContext(ProfileSetupContext);
  const resolver = useYupValidationResolver(validationSchema);
  const updateAssociate = useUpdateAssociate();

  const { handleSubmit, register, formState } =
    useForm<EmployeeIdFormFieldValues>({
      defaultValues: {
        employeeId: user?.employeeId || '',
      },
      resolver,
    });

  const { errors } = formState;

  const handleFormSubmit = (e: FormEvent) => {
    e.preventDefault();

    handleSubmit(async (formValues) => {
      const { employeeId } = formValues;

      setIsLoading(true);
      try {
        await updateAssociate.mutateAsync({ employeeId });
        await refetchUser();
        goToNextStep();
      } catch (e) {
        console.error(e);
        setError(true);
      }
      setIsLoading(false);
    })();
  };

  return (
    <Stack gap={4} sx={{ position: 'relative' }}>
      <ProfileSetupBackButton />
      <Stack gap={1} alignItems="center">
        <Typography variant="h5" textAlign="center">
          What's your Employee ID?
        </Typography>
        <Typography textAlign="center">
          This is your unique identifier withing your company.
        </Typography>
      </Stack>
      {error && <ProfileSetupError title="Employee ID" />}
      <StyledForm onSubmit={handleFormSubmit} style={{ width: '100%' }}>
        <TextField
          slotProps={{
            input: register('employeeId'),
          }}
          autoFocus
          label={`Employee ID${
            errors?.employeeId?.message ? ` - ${errors.employeeId.message}` : ''
          }`}
          fullWidth
          error={Boolean(errors.employeeId)}
        />
        <Stack gap={1}>
          <ProfileSetupContinueButton type="submit" loading={isLoading}>
            Next
          </ProfileSetupContinueButton>
          <Button variant="text" size="small" onClick={goToNextStep}>
            <Typography variant="body2" component="span" fontWeight="inherit">
              Skip
            </Typography>
          </Button>
        </Stack>
      </StyledForm>
    </Stack>
  );
};

export default EnterEmployeeId;
